import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  Divider,
  Checkbox,
  Badge,
} from '@ublend-npm/aulaui-core';
import { WidgetCard } from '../common';
import { useDispatch, useSelector } from 'react-redux';
import { toggleWidgetPreference } from '../../../../../actions/home/widgetPreferences';
import { WidgetPreference } from '@core/types/state';
import widgetsLibrary from '../../widgetsLibrary';
import { isHideableWidget } from '../../layout/widgetLayout.types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  isHomeOutlookWidgetEnabled,
  isQuickLinksInstitutionLevelEnabled,
} from '@core/constants/launchDarklyFeatureFlags';
import { useTotalUnreadCountMessages } from '../messagesWidget/useTotalUnreadCountMessages';
const BADGE_WIDGETS = ['home-messages-widget'];
const ManageWidget = () => {
  const dispatch = useDispatch();
  const { palette } = useTheme();

  const isDarkMode = palette.mode === 'dark';

  const iconBg = isDarkMode ? '#d7d2df' : '#5d536c';
  const flags = useFlags();
  const isQuickLinksEnabled = isQuickLinksInstitutionLevelEnabled(flags);
  const isOutlookWidgetEnabled = isHomeOutlookWidgetEnabled(flags);

  const widgetPreferences: WidgetPreference[] | null = useSelector(
    (state: any) => state.home?.widgetPreferences
  );

  const handleToggle = (widgetId: string, currentIsHidden: boolean) => {
    dispatch(toggleWidgetPreference(widgetId, !currentIsHidden) as any);
  };

  const messageWidgetUnreadCount = useTotalUnreadCountMessages();

  const widgetEntries = widgetsLibrary
    .filter((widget) => isHideableWidget(widget.id))
    .filter(
      (widget) => widget.id !== 'home-quick-links-widget' || isQuickLinksEnabled
    )
    .filter(
      (widget) => widget.id !== 'home-outlook-widget' || isOutlookWidgetEnabled
    )
    .reduce((acc, widget) => {
      acc[widget.id] = widget;
      return acc;
    }, {} as Record<string, any>);

  return (
    <WidgetCard title="Manage widgets">
      {Object.entries(widgetEntries).map(
        ([widgetId, widgetInfo]: any, index) => {
          const widgetPref = widgetPreferences?.find(
            (w) => w.widgetId === widgetId
          );
          const isLast = index === Object.keys(widgetEntries).length - 1;

          return (
            <React.Fragment key={widgetId}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={1}
              >
                <Box display="flex" alignItems="center" gap="16px">
                  <Box
                    width="40px"
                    height="40px"
                    borderRadius="50%"
                    bgcolor={iconBg}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={0}
                    color="white"
                    position="relative"
                  >
                    {widgetInfo.icon}
                    <Badge
                      badgeContent={messageWidgetUnreadCount}
                      color="warning"
                      invisible={
                        !BADGE_WIDGETS.includes(widgetId) ||
                        !widgetPref?.isHidden
                      }
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      color="textPrimary"
                      variant="subtitle1"
                      fontWeight="medium"
                    >
                      {widgetInfo.title}
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      Toggle to{' '}
                      {widgetPref
                        ? widgetPref.isHidden
                          ? 'unhide'
                          : 'hide'
                        : 'hide'}
                    </Typography>
                  </Box>
                </Box>
                <Checkbox
                  kind="switch"
                  checked={widgetPref ? !widgetPref.isHidden : true}
                  onChange={() =>
                    handleToggle(
                      widgetId,
                      widgetPref ? widgetPref.isHidden : false
                    )
                  }
                  aria-label="Hide/Unhide widgets"
                  sx={{ marginRight: 0 }}
                />
              </Box>
              {!isLast && (
                <Divider
                  sx={{
                    marginBlock: 1,
                  }}
                />
              )}
            </React.Fragment>
          );
        }
      )}
    </WidgetCard>
  );
};

export default ManageWidget;
